import { jsPDF } from 'jspdf';
import { BetaTestReport, DeployTest, DeployTestStatus, PublicTestResult } from '../types';
import {
  firmwareTypeLabel,
  formatQuestionAnswer,
  isFinishedDeployTest,
  releaseNoteItems,
  resolveItemStatus,
  statusMeta,
} from '../components/views/deployTestUtils';

const PAGE_WIDTH = 612;
const PAGE_HEIGHT = 792;
const MARGIN = 48;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;

const STATUS_COLORS: Record<DeployTestStatus, { fill: string; text: string }> = {
  planning: { fill: '#dbeafe', text: '#1d4ed8' },
  testing: { fill: '#fde047', text: '#854d0e' },
  failed: { fill: '#dc2626', text: '#ffffff' },
  succeeded: { fill: '#bbf7d0', text: '#15803d' },
  aborted: { fill: '#fee2e2', text: '#b91c1c' },
};

const FIRMWARE_COLORS: Record<string, string> = {
  beta: '#0d6efd',
  alpha: '#6f42c1',
  dev: '#dc3545',
};

const sanitize = (value: string) =>
  value
    .replace(/\r\n/g, '\n')
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/[—–]/g, '-')
    .replace(/[^\t\n\r\x20-\x7E]/g, '');

const formatDate = (value?: string | Date) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

const formatPerson = (test: DeployTest) => {
  const person = test.createdBy;
  if (!person) return '—';
  const name = `${person.firstName || ''} ${person.lastName || ''}`.trim();
  if (name && person.email) return `${name} (${person.email})`;
  return name || person.email || '—';
};

const slugify = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'deploy-test';

const taskStatusLabel = (test: DeployTest, item: DeployTest['items'][number]) => {
  if (test.status === 'planning') return 'Planning';
  if (test.status === 'aborted') return 'Aborted';
  const status = resolveItemStatus(item);
  if (status === 'passed') return 'Passed';
  if (status === 'flagged') return 'Flagged';
  if (status === 'failed') return 'Failed';
  return 'Testing';
};

const testerName = (report: BetaTestReport | PublicTestResult) => {
  if ('tester' in report) return report.tester || 'Tester';
  const user = report.user;
  if (!user) return 'Tester';
  const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  return name || user.email || 'Tester';
};

const testerPhone = (report: BetaTestReport | PublicTestResult) => {
  if ('tester' in report) return report.phone || 'Phone';
  const name = report.phoneDeviceName || 'Phone';
  const platform = report.phoneDevicePlatform === 'ios'
    ? 'iOS'
    : report.phoneDevicePlatform === 'android'
      ? 'Android'
      : '';
  return platform ? `${name} (${platform})` : name;
};

export const downloadDeployTestPdf = (test: DeployTest, reports: Array<BetaTestReport | PublicTestResult> = []) => {
  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  let y = MARGIN;

  const fillText = () => {
    doc.setLineWidth(0);
    const pdfInternal = doc.internal as { write?: (value: string) => void };
    pdfInternal.write?.('0 Tr');
  };

  const ensureSpace = (needed: number) => {
    if (y + needed <= PAGE_HEIGHT - MARGIN) return;
    doc.addPage();
    y = MARGIN;
  };

  const addWrapped = (text: string, fontSize: number, font: 'normal' | 'bold' = 'normal', color = '#111827') => {
    const cleaned = sanitize(text || '') || '—';
    fillText();
    doc.setFont('helvetica', font);
    doc.setFontSize(fontSize);
    doc.setTextColor(color);
    const lines = doc.splitTextToSize(cleaned, CONTENT_WIDTH) as string[];
    const lineHeight = fontSize + 5;
    lines.forEach((line) => {
      ensureSpace(lineHeight);
      fillText();
      doc.text(line, MARGIN, y);
      y += lineHeight;
    });
  };

  const addSection = (title: string) => {
    y += 16;
    ensureSpace(32);
    fillText();
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor('#6b7280');
    doc.text(title.toUpperCase(), MARGIN, y);
    y += 8;
    doc.setDrawColor('#d1d5db');
    doc.setLineWidth(0.75);
    doc.line(MARGIN, y, PAGE_WIDTH - MARGIN, y);
    fillText();
    y += 16;
  };

  const addField = (label: string, value: string) => {
    ensureSpace(16);
    fillText();
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor('#6b7280');
    doc.text(`${label}:`, MARGIN, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor('#111827');
    const labelWidth = doc.getTextWidth(`${label}:  `);
    const lines = doc.splitTextToSize(sanitize(value) || '—', CONTENT_WIDTH - labelWidth) as string[];
    lines.forEach((line, index) => {
      if (index > 0) {
        ensureSpace(14);
      }
      fillText();
      doc.text(line, MARGIN + (index === 0 ? labelWidth : 0), y);
      y += 14;
    });
  };

  fillText();
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor('#d97706');
  doc.text('BETA DEPLOY TEST', MARGIN, y);
  y += 22;

  addWrapped(test.name || 'Untitled test', 22, 'bold');
  y += 16;

  const columnWidth = (CONTENT_WIDTH - 24) / 3;
  const firmwareLabel = firmwareTypeLabel(test.firmwareType);
  const firmwareValue = test.firmwareVersion ? `v${test.firmwareVersion}` : 'Not selected';
  const iosAppValue = test.mobileAppVersionIos || test.mobileAppVersion ? `v${test.mobileAppVersionIos || test.mobileAppVersion}` : 'Not selected';
  const androidAppValue = test.mobileAppVersionAndroid || test.mobileAppVersion ? `v${test.mobileAppVersionAndroid || test.mobileAppVersion}` : 'Not selected';
  const firmwareColor = test.firmwareVersion
    ? (FIRMWARE_COLORS[test.firmwareType || ''] || '#111827')
    : '#6b7280';
  const iosAppColor = (test.mobileAppVersionIos || test.mobileAppVersion) ? '#111827' : '#6b7280';
  const androidAppColor = (test.mobileAppVersionAndroid || test.mobileAppVersion) ? '#111827' : '#6b7280';

  ensureSpace(56);
  fillText();
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor('#6b7280');
  doc.text('FIRMWARE', MARGIN, y);
  doc.text('MOBILE APP (IOS)', MARGIN + columnWidth + 12, y);
  doc.text('MOBILE APP (ANDROID)', MARGIN + (columnWidth + 12) * 2, y);
  y += 20;

  fillText();
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(firmwareColor);
  doc.text(sanitize(firmwareValue), MARGIN, y);
  doc.setTextColor(iosAppColor);
  doc.text(sanitize(iosAppValue), MARGIN + columnWidth + 12, y);
  doc.setTextColor(androidAppColor);
  doc.text(sanitize(androidAppValue), MARGIN + (columnWidth + 12) * 2, y);

  if (firmwareLabel && test.firmwareType) {
    const valueWidth = doc.getTextWidth(sanitize(firmwareValue));
    const badge = firmwareLabel.toUpperCase();
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    const badgeWidth = doc.getTextWidth(badge) + 12;
    const badgeX = MARGIN + valueWidth + 10;
    const badgeY = y - 12;
    doc.setFillColor(firmwareColor);
    doc.roundedRect(badgeX, badgeY, badgeWidth, 14, 7, 7, 'F');
    fillText();
    doc.setTextColor('#ffffff');
    doc.text(badge, badgeX + 6, y - 2);
  }
  y += 28;

  const status = statusMeta(test.status);
  const statusColors = STATUS_COLORS[test.status] || STATUS_COLORS.planning;
  ensureSpace(42);
  doc.setFillColor(statusColors.fill);
  doc.roundedRect(MARGIN, y, CONTENT_WIDTH, 36, 8, 8, 'F');
  fillText();
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(statusColors.text);
  doc.text(status.label.toUpperCase(), PAGE_WIDTH / 2, y + 23, { align: 'center' });
  y += 52;

  addField('Created', formatDate(test.createdAt));
  addField('Updated', formatDate(test.updatedAt));
  addField('Created by', formatPerson(test));

  addSection('Instructions');
  addWrapped(test.overview?.trim() || 'None', 10);

  addSection('Release Notes');
  const notes = releaseNoteItems(test.releaseNotes);
  if (!notes.length) {
    addWrapped('None', 10, 'normal', '#6b7280');
  } else {
    notes.forEach((note) => {
      addWrapped(`• ${note}`, 10);
    });
  }

  const items = test.items || [];
  const passedCount = items.filter((item) => resolveItemStatus(item) === 'passed').length;
  addSection(`Tasks (${passedCount} of ${items.length} complete)`);

  if (items.length === 0) {
    addWrapped('No tasks yet', 10, 'normal', '#6b7280');
  } else {
    items.forEach((item, index) => {
      const title = `${index + 1}. ${item.text || 'Untitled task'}`;
      const itemStatus = taskStatusLabel(test, item);
      fillText();
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      const titleLines = doc.splitTextToSize(sanitize(title), CONTENT_WIDTH - 90) as string[];
      const blockHeight = titleLines.length * 15 + (item.description?.trim() ? 36 : 18);
      ensureSpace(blockHeight);

      const titleTop = y;
      titleLines.forEach((line) => {
        fillText();
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.setTextColor('#111827');
        doc.text(line, MARGIN, y);
        y += 15;
      });

      fillText();
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor('#374151');
      doc.text(itemStatus.toUpperCase(), PAGE_WIDTH - MARGIN, titleTop, { align: 'right' });

      if (item.description?.trim()) {
        y += 2;
        addWrapped(item.description, 10, 'normal', '#374151');
      }
      y += 10;
    });
  }

  const questions = test.questions || [];
  addSection('Questions');
  if (questions.length === 0) {
    addWrapped('No questions yet', 10, 'normal', '#6b7280');
  } else {
    questions.forEach((question, index) => {
      const typeLabel = question.type === 'boolean'
        ? 'Yes / No'
        : question.type === 'choice'
          ? 'Multiple choice'
          : 'Text';
      addWrapped(`${index + 1}. ${question.prompt || 'Untitled question'} (${typeLabel})`, 11, 'bold');
      if (question.type === 'choice' && question.choices?.length) {
        addWrapped(`Choices: ${question.choices.filter(Boolean).join(', ')}`, 10, 'normal', '#374151');
      }
      if (question.type !== 'text' && question.additionalInfo?.trim()) {
        addWrapped(`Additional Info: ${question.additionalInfo}`, 10, 'normal', '#374151');
      }
      y += 8;
    });
  }

  addSection('Kickoff Email');
  addWrapped(test.kickoffEmail?.trim() || 'None', 10);

  if (isFinishedDeployTest(test.status)) {
    const completed = reports.filter((report) => (
      report.status === 'completed' || report.status === 'rejected'
    ));
    addSection(`Answers & Feedback (${completed.length})`);
    if (!completed.length) {
      addWrapped('No completed tester responses yet', 10, 'normal', '#6b7280');
    } else {
      completed.forEach((report, index) => {
        addWrapped(`${index + 1}. ${testerName(report)} · ${testerPhone(report)}`, 12, 'bold');
        const tasks = report.taskResponses || [];
        if (tasks.length) {
          addWrapped('Tasks', 10, 'bold', '#6b7280');
          tasks.forEach((task) => {
            const label = 'text' in task ? task.text : 'Task';
            addWrapped(`${task.done ? '[x]' : '[ ]'} ${label || 'Task'}`, 10);
          });
        }
        const answers = report.questionResponses || [];
        if (answers.length) {
          addWrapped('Answers', 10, 'bold', '#6b7280');
          answers.forEach((answer) => {
            const prompt = 'prompt' in answer ? answer.prompt : 'Question';
            addWrapped(prompt || 'Question', 10, 'bold');
            addWrapped(formatQuestionAnswer(answer.value, 'type' in answer ? answer.type : undefined), 10);
            if (answer.additionalInfo?.trim()) {
              addWrapped(answer.additionalInfo, 10, 'normal', '#374151');
            }
          });
        }
        y += 8;
      });
    }
  }

  doc.save(`${slugify(test.name)}.pdf`);
};
